import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils/cn'
import { useSignInMutation } from '../api/use-sign-in-mutation'

// Show quick-login when the Vite dev server is running OR the app was built in
// debug mode (VITE_APP_MODE=debug, baked at build time) — so a deployed *debug*
// build keeps the buttons for shared testing. A prod build strips this branch.
const IS_DEV = import.meta.env.DEV || import.meta.env.VITE_APP_MODE === 'debug'

const DEV_USERS = [
  { label: 'Super User', email: 'super@bluedatalake.local', password: 'password123', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30 hover:bg-purple-500/25' },
  { label: 'Admin', email: 'admin@bluedatalake.local', password: 'password123', color: 'bg-primary/15 text-primary border-primary/30 hover:bg-primary/25' },
  { label: 'Approver', email: 'approver@bluedatalake.local', password: 'password123', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25' },
  { label: 'Data Entry', email: 'dataentry@bluedatalake.local', password: 'password123', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/25' },
] as const

const signInSchema = z.object({
  email: z.string().email('ایمیل نامعتبر است'),
  password: z.string().min(6, 'رمز عبور باید حداقل ۶ کاراکتر باشد'),
  rememberMe: z.boolean().optional(),
})

type SignInFormData = z.infer<typeof signInSchema>

export function SignInPage() {
  const { t } = useTranslation()
  const mutation = useSignInMutation()

  const form = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: 'dataentry@bluedatalake.local',
      password: 'password123',
      rememberMe: false,
    },
  })

  const onSubmit = (data: SignInFormData) => {
    mutation.mutate({ email: data.email, password: data.password })
  }

  const errorMessage = mutation.error
    ? (mutation.error as Error).message || 'خطا در ورود. لطفاً دوباره تلاش کنید.'
    : null

  return (
    <div className="relative min-h-screen bg-background flex items-center justify-center overflow-hidden">
      {/* Background blobs */}
      <div
        className="absolute top-0 start-0 w-96 h-96 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'rgba(39,70,144,0.20)', transform: 'translate(-30%, -30%)' }}
      />
      <div
        className="absolute bottom-0 end-0 w-80 h-80 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'rgba(40,111,108,0.20)', transform: 'translate(30%, 30%)' }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[420px] mx-4">
        <div className="bg-card border border-border rounded-xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mb-4">
              <span className="text-primary-foreground font-bold text-xl font-mono">M</span>
            </div>
            <h1 className="text-xl font-bold text-foreground">
              {t('appTitle')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('auth.signIn.subtitle', 'ورود به پنل مدیریت')}
            </p>
          </div>

          {/* Error banner */}
          {errorMessage && (
            <div className="flex items-center gap-2 bg-destructive/15 border border-destructive/30 rounded-lg px-4 py-3 mb-4 text-sm text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.signIn.email', 'ایمیل')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="example@hospital.ir"
                        autoComplete="email"
                        className="font-mono text-sm ltr"
                        dir="ltr"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>{t('auth.signIn.password', 'رمز عبور')}</FormLabel>
                      <Link
                        to="/forgot-password"
                        className="text-xs text-primary hover:text-primary/80 transition-colors"
                      >
                        {t('auth.signIn.forgotPassword', 'فراموشی رمز عبور؟')}
                      </Link>
                    </div>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        autoComplete="current-password"
                        dir="ltr"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal text-sm cursor-pointer">
                      {t('auth.signIn.rememberMe', 'مرا به خاطر بسپار')}
                    </FormLabel>
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                size="lg"
                className="w-full mt-2"
                disabled={mutation.isPending}
              >
                {mutation.isPending
                  ? t('auth.signIn.signingIn', 'در حال ورود...')
                  : t('auth.signIn.submit', 'ورود')}
              </Button>
            </form>
          </Form>
          {IS_DEV && (
            <div className="mt-5 pt-4 border-t border-border/50">
              <p className="text-[11px] text-muted-foreground text-center mb-2 font-mono">DEV — quick login</p>
              <div className="grid grid-cols-2 gap-1.5">
                {DEV_USERS.map((u) => (
                  <button
                    key={u.email}
                    type="button"
                    className={`text-xs px-2 py-1.5 rounded-md border font-medium transition-colors ${u.color}`}
                    onClick={() => {
                      form.setValue('email', u.email)
                      form.setValue('password', u.password)
                    }}
                  >
                    {u.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
